import { PublicClientApplication } from '@azure/msal-browser';
import { app, authentication } from '@microsoft/teams-js';
import { loginRequest, msalConfig } from '@/lib/authConfig';

/** Marks the URL Teams opens in the sign-in popup (`authentication.authenticate`). */
const POPUP_QUERY_KEY = 'teams-auth';
const LOGIN_HINT_QUERY_KEY = 'loginHint';
/**
 * Survives the round trip to Microsoft login inside the popup window. sessionStorage is scoped to that
 * window, so the Teams tab itself never sees it and keeps rendering the app.
 */
const POPUP_FLAG_KEY = 'teams_auth_popup';

const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 700;

/** True in the window Teams opened for interactive sign-in, both on its first page and on the return from Microsoft. */
export function isTeamsAuthPopup(): boolean {
    try {
        return new URLSearchParams(window.location.search).has(POPUP_QUERY_KEY) || sessionStorage.getItem(POPUP_FLAG_KEY) === '1';
    } catch {
        return false;
    }
}

/**
 * Interactive sign-in for a Teams tab whose silent SSO needs the user (`login_required`, consent, ...).
 * MSAL refuses redirects inside the tab's iframe, so Teams opens a popup (a top-level window) on this same
 * origin that runs {@link runTeamsAuthPopup}; resolves with the id token that popup hands back.
 */
export async function authenticateWithTeamsPopup(loginHint: string): Promise<string> {
    const url = `${window.location.origin}/?${POPUP_QUERY_KEY}=popup&${LOGIN_HINT_QUERY_KEY}=${encodeURIComponent(loginHint)}`;
    try {
        const idToken = (await authentication.authenticate({ url, width: POPUP_WIDTH, height: POPUP_HEIGHT }))?.trim();
        if (!idToken) {
            throw new Error('Teams sign-in did not return an ID token');
        }
        return idToken;
    } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(typeof error === 'string' && error ? error : 'Teams sign-in failed');
    }
}

/**
 * Runs inside the popup. First load: redirects to Microsoft login (allowed in a top-level window).
 * Return from Microsoft: reads the id token and hands it to the Teams tab.
 */
export async function runTeamsAuthPopup(): Promise<void> {
    const clearFlag = () => {
        try {
            sessionStorage.removeItem(POPUP_FLAG_KEY);
        } catch {
            // storage unavailable — nothing to clear
        }
    };

    try {
        await app.initialize();

        // Own instance: this window must resolve the redirect right where Microsoft returns (the origin root)
        // instead of navigating on to the page that started the login, which the app's instance would do.
        const instance = new PublicClientApplication({
            ...msalConfig,
            auth: { ...msalConfig.auth, navigateToLoginRequestUrl: false },
        });
        await instance.initialize();

        const result = await instance.handleRedirectPromise();
        const idToken = result?.idToken?.trim();
        if (idToken) {
            clearFlag();
            authentication.notifySuccess(idToken);
            return;
        }

        const loginHint = new URLSearchParams(window.location.search).get(LOGIN_HINT_QUERY_KEY) ?? undefined;
        sessionStorage.setItem(POPUP_FLAG_KEY, '1');
        await instance.loginRedirect({ ...loginRequest, loginHint });
    } catch (error) {
        clearFlag();
        authentication.notifyFailure(error instanceof Error ? error.message : 'Microsoft sign-in failed');
    }
}
